import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, TextInput, View, type TextStyle } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { ControlAccessGate } from "@/src/control/ControlAccessGate";
import { ControlDisclosure, ControlId, ControlKeyValue } from "@/src/control/ControlDetails";
import { listControlAuditLog, type ControlAuditEvent } from "@/src/control/api";
import { contentMaxWidths, contentPage } from "@/src/theme/layout";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";
import { auditActionLabel, auditSourceLabel, controlRoleLabel, formatControlDate, humanizeEnum, moderationTargetLabel } from "@/src/control/presentation";

const breakableTextStyle: TextStyle | undefined = Platform.OS === "web"
  ? ({ overflowWrap: "anywhere", wordBreak: "break-word" } as unknown as TextStyle)
  : undefined;

type AuditSourceFilter = "all" | ControlAuditEvent["source"];

const filters: { value: AuditSourceFilter; label: string }[] = [
  { value: "all", label: "Tous" },
  { value: "control_center", label: "Control Center" },
  { value: "system", label: "Système" },
  { value: "bootstrap", label: "Initialisation" },
];

export default function ControlAuditScreen() {
  return <ControlAccessGate permission="control.audit.read"><Audit /></ControlAccessGate>;
}

function Audit() {
  const [events, setEvents] = useState<ControlAuditEvent[]>([]);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<AuditSourceFilter>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    listControlAuditLog().then((value) => active && setEvents(value)).catch(() => active && setError(true)).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [attempt]);

  const filteredEvents = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("fr-FR");
    return events.filter((event) => {
      if (source !== "all" && event.source !== source) return false;
      if (!normalizedQuery) return true;
      const haystack = [event.action, event.actorUid, event.targetType, event.targetId, event.reason, event.reportId, JSON.stringify(event.metadata || {})].filter(Boolean).join(" ").toLocaleLowerCase("fr-FR");
      return haystack.includes(normalizedQuery);
    });
  }, [events, query, source]);

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.header}>
        <Text style={styles.kicker}>SUIVI</Text>
        <Text style={styles.title}>Journal d’activité</Text>
        <Text style={styles.subtitle}>Retrouve les actions récentes, leur auteur et leur cible. Les détails techniques restent masqués jusqu’à ce qu’ils soient utiles.</Text>
      </View>

      <TextInput accessibilityLabel="Rechercher dans le journal" onChangeText={setQuery} placeholder="Rechercher une action, un acteur ou une cible" placeholderTextColor={text.muted} style={styles.search} value={query} />
      <ScrollView horizontal style={styles.filterRail} contentContainerStyle={styles.filters} showsHorizontalScrollIndicator={false}>
        {filters.map((item) => <Pressable accessibilityRole="tab" accessibilityState={{ selected: source === item.value }} key={item.value} onPress={() => setSource(item.value)} style={[styles.filter, source === item.value && styles.filterActive]}><Text style={[styles.filterText, source === item.value && styles.filterTextActive]}>{item.label}</Text></Pressable>)}
      </ScrollView>

      {!loading && !error ? <Text style={styles.count}>{filteredEvents.length} événement{filteredEvents.length === 1 ? "" : "s"} affiché{filteredEvents.length === 1 ? "" : "s"}</Text> : null}
      {loading ? <ActivityIndicator color={text.brand} /> : error ? <Pressable accessibilityRole="button" onPress={() => setAttempt((value) => value + 1)} style={styles.state}><Text style={styles.stateTitle}>Journal indisponible</Text><Text style={styles.stateBody}>Chargement impossible. Réessayer.</Text></Pressable> : !filteredEvents.length ? <Text style={styles.empty}>{events.length ? "Aucun événement ne correspond à ces critères." : "Aucun événement."}</Text> : filteredEvents.map((event) => <AuditEventRow event={event} key={event.id} />)}
    </ScrollView>
  );
}

function AuditEventRow({ event }: { event: ControlAuditEvent }) {
  const actor = event.actorUid ? `${event.actorRole ? controlRoleLabel(event.actorRole) : "Membre Control"}` : auditSourceLabel(event.source);
  const target = event.targetType ? moderationTargetLabel(event.targetType) : "Cible non précisée";
  const summary = event.reason || (event.previousState || event.newState ? `${humanizeEnum(event.previousState, "État initial")} → ${humanizeEnum(event.newState, "état final")}` : "Événement enregistré.");
  return (
    <View style={styles.event}>
      <View style={styles.eventHeader}>
        <Text style={[styles.action, breakableTextStyle]}>{auditActionLabel(event.action)}</Text>
        <Text style={styles.sourceBadge}>{auditSourceLabel(event.source)}</Text>
      </View>
      <Text style={styles.timestamp}>{formatControlDate(event.createdAt)}</Text>
      <View style={styles.keyValues}>
        <ControlKeyValue label="Acteur" value={actor} />
        <ControlKeyValue label="Cible" value={target} />
      </View>
      <Text style={[styles.summary, breakableTextStyle]}>{summary}</Text>
      <ControlDisclosure>
        <ControlId label="ID de l’événement" value={event.id} />
        <ControlId label="UID de l’acteur" value={event.actorUid} />
        <ControlId label="ID de la cible" value={event.targetId} />
        <ControlId label="ID du signalement" value={event.reportId} />
        <ControlKeyValue label="Action technique" value={event.action} />
        {event.previousState || event.newState ? <ControlKeyValue label="Transition" value={`${event.previousState || "—"} → ${event.newState || "—"}`} /> : null}
        {event.metadata ? <View style={styles.metadata}><Text style={styles.metadataLabel}>Métadonnées</Text><Text selectable style={[styles.metadataValue, breakableTextStyle]}>{JSON.stringify(event.metadata, null, 2)}</Text></View> : null}
      </ControlDisclosure>
    </View>
  );
}

const styles = StyleSheet.create({
  page: { ...contentPage(contentMaxWidths.control), gap: spacing.lg },
  header: { gap: spacing.sm },
  kicker: { color: text.brand, fontSize: 12, fontWeight: "800", letterSpacing: 1.5 },
  title: { color: text.primary, fontSize: 28, fontWeight: "800" },
  subtitle: { color: text.secondary, lineHeight: 22, minWidth: 0 },
  search: { borderColor: border.strong, borderRadius: radius.md, borderWidth: 1, color: text.primary, minHeight: 48, paddingHorizontal: spacing.md },
  filterRail: { flexGrow: 0, maxWidth: "100%", width: "100%" },
  filters: { gap: spacing.sm },
  filter: { borderColor: border.subtle, borderRadius: radius.pill, borderWidth: 1, minHeight: 44, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  filterActive: { backgroundColor: surface.selected, borderColor: border.brand },
  filterText: { color: text.secondary, fontWeight: "700" },
  filterTextActive: { color: text.brand },
  count: { color: text.muted, fontSize: 12 },
  empty: { color: text.secondary, paddingVertical: spacing.lg },
  state: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.xs, padding: spacing.lg },
  stateTitle: { color: text.primary, fontSize: 16, fontWeight: "800" },
  stateBody: { color: text.secondary },
  event: { backgroundColor: surface.primary, borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.md, minWidth: 0, padding: spacing.lg },
  eventHeader: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, justifyContent: "space-between", minWidth: 0 },
  action: { color: text.primary, flex: 1, flexShrink: 1, fontSize: 16, fontWeight: "800", minWidth: 0 },
  sourceBadge: { backgroundColor: surface.selected, borderRadius: radius.pill, color: text.secondary, fontSize: 11, fontWeight: "700", overflow: "hidden", paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  timestamp: { color: text.muted, fontSize: 12 },
  keyValues: { gap: spacing.md },
  summary: { color: text.primary, lineHeight: 21, minWidth: 0 },
  metadata: { gap: spacing.xs, minWidth: 0 },
  metadataLabel: { color: text.muted, fontSize: 11, fontWeight: "700" },
  metadataValue: { color: text.secondary, fontFamily: "monospace", fontSize: 12, lineHeight: 18, minWidth: 0 },
});
